import { Controller } from '@hotwired/stimulus';
import AutoSave from 'stimulus/auto_save';

// The chips of a multiple field (Ui::SelectFieldComponent, Ui::LocationFieldComponent). The field
// stays a one-at-a-time picker: each pick becomes a chip with its own hidden input, or one per
// field for a pick that carries several, and the option is taken out of the menu until the chip is
// removed. The picks reach this controller as the field's own events, listened for on its root,
// which wraps this element.
export default class extends Controller {
  static targets = ['values', 'chips', 'chipTemplate'];

  static values = {
    name: String,
    autoSaveUrl: String,
    freeText: Boolean,
    source: { type: String, default: 'select-field-component' },
    max: { type: Number, default: 0 },
    debounce: { type: Number, default: 500 }
  };

  connect() {
    this.handlePick = this.pick.bind(this);
    this.handleCreate = this.create.bind(this);
    this.field = this.element.closest(`[data-controller~="${this.sourceValue}"]`);
    this.field?.addEventListener(`${this.sourceValue}:change`, this.handlePick);
    // Free text turns the select's "nothing matched" row into a chip of the typed text.
    if (this.freeTextValue) this.field?.addEventListener(`${this.sourceValue}:create`, this.handleCreate);

    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      name: this.nameValue,
      debounce: this.debounceValue,
      label: this.identifier,
      element: this.element
    });
  }

  disconnect() {
    this.field?.removeEventListener(`${this.sourceValue}:change`, this.handlePick);
    this.field?.removeEventListener(`${this.sourceValue}:create`, this.handleCreate);
    this.autoSave?.cancel();
  }

  pick(event) {
    const { value, label, fields } = event.detail;
    this.add(value, label, fields);
  }

  create(event) {
    const query = event.detail.query.trim();
    this.add(query, query);
  }

  add(value, label, fields = {}) {
    if (!value) return;

    const field = this.selectField();
    if (this.hasValue(value) || this.full()) {
      field?.reset();
      return;
    }

    this.appendChip(value, label, fields);
    field?.take?.(value);
    field?.reset();
    this.chipsTarget.hidden = false;
    this.announceChange();
    this.dispatch('added', { detail: { value, label } });
  }

  remove(event) {
    this.drop(String(event.params.value));
  }

  // Removal by value rather than by click, so a controller pairing two of these can take a
  // duplicate out of one side. `remove` is the same operation reached from the chip.
  drop(value, { notify = true } = {}) {
    if (!value) return;

    const item = this.chipFor(value);
    if (!item) return;

    const label = item.querySelector('.ui-chip__label').textContent.trim();

    item.remove();
    this.selectField()?.release?.(value, label);

    if (!this.chipsTarget.querySelector('.ui-selected-chips__item')) {
      this.chipsTarget.hidden = true;
    }

    if (notify) this.announceChange();
    this.dispatch('removed', { detail: { value, label } });
  }

  // One save for the whole field, rather than one per value on the way down.
  clear() {
    const values = this.values();
    if (values.length === 0) return;

    values.forEach(value => this.drop(value, { notify: false }));
    this.announceChange();
  }

  take(value) {
    if (!this.hasValue(value)) this.selectField()?.take?.(value);
  }

  release(value, label) {
    if (!this.hasValue(value)) this.selectField()?.release?.(value, label);
  }

  values() {
    return Array.from(this.chipsTarget.querySelectorAll('.ui-selected-chips__item'), item => item.dataset.value)
      .filter(Boolean);
  }

  full() {
    return this.maxValue > 0 && this.values().length >= this.maxValue;
  }

  chipFor(value) {
    const selector = `[data-selected-chips-component-value-param="${CSS.escape(value)}"]`;

    return this.chipsTarget.querySelector(selector)?.closest('.ui-selected-chips__item');
  }

  // A pick that carries several fields fills one hidden input per field; any other fills the one.
  appendChip(value, label, fields) {
    const item = this.chipTemplateTarget.content.firstElementChild.cloneNode(true);
    item.dataset.value = value;
    item.querySelectorAll('.ui-selected-chips__value').forEach((input) => {
      input.value = input.dataset.field ? (fields[input.dataset.field] ?? '') : value;
    });
    item.querySelector('.ui-chip__label').textContent = label;

    const removeButton = item.querySelector('.ui-micro-button');
    removeButton.setAttribute('aria-label', `Remove ${label}`);
    removeButton.setAttribute(`data-${this.identifier}-value-param`, value);

    this.chipsTarget.appendChild(item);
  }

  // Fired from the blank input the list carries, which outlives every value.
  announceChange() {
    (this.valuesTarget.querySelector('input') || this.valuesTarget)
      .dispatchEvent(new Event('change', { bubbles: true }));

    if (this.autoSaveUrlValue) this.autoSave.saveNow(this.submittedValues());
  }

  // The blank sentinel first, then one value per chip, as the form would submit them.
  submittedValues() {
    return ['', ...this.values()];
  }

  hasValue(value) {
    return Boolean(this.chipFor(value));
  }

  selectField() {
    if (!this.field) return null;
    return this.application.getControllerForElementAndIdentifier(this.field, this.sourceValue);
  }
}
